import { Button, Flex, FormControl, FormHelperText, FormLabel, Icon, Input, Select, useToast } from "@chakra-ui/react";
import { t } from "i18next";
import { Dispatch, SetStateAction, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { v4 as uuid } from "uuid";
import {
  CreateProjectRequest,
  PersonDto,
  PersonSearchCapability,
  ProjectDto,
  useCreateProjectMutation,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import {
  initialProjectDepartmentId,
  projectDepartmentOptions,
} from "../../common/departments/eventCreationDepartments";
import { showsProjectDepartment } from "../../common/departments/departmentVisibility";
import { DateFormControl } from "../../common/input/DateTimeSelectors/DateFormControl";
import { SingleSelector } from "../../common/input/Selector/SingleSelector";
import { PersonSelector } from "../../common/persons/PersonSelector";
import { ProjectSelector } from "./ProjectSelector";
import { useProjectNavigation } from "./useProjectNavigation";
import { useProjectTypes } from "./useProjectTypes";

const initializeProject = (): CreateProjectRequest => ({
  id: uuid(),
  externalId: "",
  name: "",
  startDate: undefined,
  endDate: undefined,
  parent: undefined,
  projectResponsible: "",
  sourcingResponsible: "",
});

export const CreateProjectForm = ({ setShowModal }: { setShowModal: Dispatch<SetStateAction<boolean>> }) => {
  const goToProject = useProjectNavigation();
  const [createProject, { isLoading: isCreatingProject }] = useCreateProjectMutation();
  const projectTypes = useProjectTypes();
  const toast = useToast();
  const authState = useLoggedInWithOrgContextState();

  const departments = projectDepartmentOptions(authState.selectedOrg);
  const showsPicker = showsProjectDepartment(authState.selectedOrg);
  const [newProject, setNewProject] = useState<CreateProjectRequest>(() => ({
    ...initializeProject(),
    homeDepartmentId: initialProjectDepartmentId(authState.selectedOrg),
  }));
  const [parentProject, setParentProject] = useState<ProjectDto | null>();
  const [projectResponsible, setProjectResponsible] = useState<PersonDto>();
  const [sourcingResponsible, setSourcingResponsible] = useState<PersonDto>();
  const homeDepartmentId = parentProject ? parentProject.homeDepartment?.id : newProject.homeDepartmentId;

  const clearResponsiblesUnlessStillIn = (nextHomeDepartmentId: string | null | undefined) => {
    if ((nextHomeDepartmentId ?? null) === (homeDepartmentId ?? null)) return;
    setProjectResponsible(undefined);
    setSourcingResponsible(undefined);
    setNewProject((p) => ({ ...p, projectResponsible: undefined, sourcingResponsible: undefined }));
  };

  return (
    <>
      <Flex grow={1} flexDirection="column" overflowY="scroll" shadow="inner" rounded="lg" p="4">
        <FormControl pb="4">
          <FormLabel fontSize="sm">{t("Project ID")}</FormLabel>
          <Input
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            placeholder={`${t("Project ID")}...`}
            value={newProject.externalId ?? undefined}
            onChange={(e) => setNewProject((p) => ({ ...p, externalId: e.target.value }))}
          />
          <FormHelperText>{`${t("Enter your unique project identifier")} (${t("optional")}).`}</FormHelperText>
        </FormControl>
        <FormControl pb="4">
          <FormLabel fontSize="sm">{t("Name")}</FormLabel>
          <Input
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            placeholder={`${t("Name")}...`}
            value={newProject.name}
            onChange={(e) => setNewProject((p) => ({ ...p, name: e.target.value }))}
          />
          <FormHelperText>{`${t("Enter a descriptive project name")}.`}</FormHelperText>
        </FormControl>
        <FormControl pb="4">
          <FormLabel fontSize="sm">{t("Project type")}</FormLabel>
          <SingleSelector
            size="sm"
            placeholder={{ text: `${t("Project type")}...`, color: "" }}
            value={projectTypes.find((t) => t.value === newProject.projectType)}
            options={projectTypes}
            onChange={(option) => setNewProject((p) => ({ ...p, projectType: option?.value }))}
          />
          <FormHelperText>{`${t("Select the project type")}.`}</FormHelperText>
        </FormControl>
        <Flex pb="4">
          <DateFormControl
            id="project start date"
            title={t("Start date")}
            isRequiredButNotProvided={false}
            defaultDate={newProject.startDate ?? undefined}
            helperText={`${t("Select the project start date")}.`}
            errorMessage={null}
            onChange={(d) => setNewProject((p) => ({ ...p, startDate: d ?? undefined }))}
          />
        </Flex>
        <Flex pb="4">
          <DateFormControl
            id="project end date"
            title={t("End date")}
            isRequiredButNotProvided={false}
            defaultDate={newProject.endDate ?? undefined}
            helperText={`${t("Select the project end date")}.`}
            errorMessage={null}
            onChange={(d) => setNewProject((p) => ({ ...p, endDate: d ?? undefined }))}
          />
        </Flex>
        <FormControl pb="4">
          <FormLabel fontSize="sm">{t("Parent project")}</FormLabel>
          <ProjectSelector
            onlyEditable
            selectedProject={parentProject}
            onChange={(parent) => {
              clearResponsiblesUnlessStillIn(parent ? parent.homeDepartment?.id : newProject.homeDepartmentId);
              setParentProject(parent);
              setNewProject((p) => ({ ...p, parent: parent?.id }));
            }}
          />
          <FormHelperText>{`${t("Select the parent project")}.`}</FormHelperText>
        </FormControl>
        {showsPicker && (
          <FormControl pb="4" isDisabled={!!parentProject}>
            <FormLabel fontSize="sm">{t("Department")}</FormLabel>
            <Select
              size="sm"
              rounded="md"
              backgroundColor="smBackground"
              value={homeDepartmentId ?? ""}
              placeholder={t("Select department") ?? undefined}
              onChange={(e) => {
                const next = e.target.value || undefined;
                clearResponsiblesUnlessStillIn(next);
                setNewProject((p) => ({ ...p, homeDepartmentId: next }));
              }}
            >
              {(parentProject
                ? parentProject.homeDepartment
                  ? [parentProject.homeDepartment]
                  : []
                : departments
              ).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </Select>
            <FormHelperText>
              {parentProject
                ? `${t("A sub-project belongs to the same department as its parent project")}.`
                : `${t("Decides who in the organization can see the project")}.`}
            </FormHelperText>
          </FormControl>
        )}
        <FormControl pb="4">
          <FormLabel fontSize="sm">{t("Project responsible")}</FormLabel>
          <PersonSelector
            organizationIds={[authState.selectedOrg.id]}
            selectedPerson={projectResponsible}
            placeholder={`${t("Project responsible")}...`}
            capability={PersonSearchCapability.ViewProject}
            departmentId={homeDepartmentId ?? null}
            onChange={(person) => {
              setProjectResponsible(person ?? undefined);
              setNewProject((p) => ({ ...p, projectResponsible: person?.id }));
            }}
          />
          <FormHelperText>{`${t("Select the person responsible for the project")}. ${t(
            "Only users with project access can be selected"
          )}.`}</FormHelperText>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">{t("Sourcing responsible")}</FormLabel>
          <PersonSelector
            organizationIds={[authState.selectedOrg.id]}
            selectedPerson={sourcingResponsible}
            placeholder={`${t("Sourcing responsible")}...`}
            capability={PersonSearchCapability.ManageSourcingEvent}
            departmentId={homeDepartmentId ?? null}
            onChange={(person) => {
              setSourcingResponsible(person ?? undefined);
              setNewProject((p) => ({ ...p, sourcingResponsible: person?.id }));
            }}
          />
          <FormHelperText>{`${t("Select the sourcing responsible for the project")}. ${t(
            "Only users who can create procurements are listed"
          )}.`}</FormHelperText>
        </FormControl>
      </Flex>
      <Flex pt="4">
        <Button
          size={"sm"}
          variant={"outline"}
          colorScheme="teal"
          leftIcon={<Icon as={FaCheck} />}
          isDisabled={!newProject.name || (!parentProject && !newProject.homeDepartmentId)}
          isLoading={isCreatingProject}
          onClick={async () => {
            const res = await createProject({
              createProjectRequest: {
                id: newProject.id ? newProject.id : undefined,
                externalId: newProject.externalId ? newProject.externalId : undefined,
                name: newProject.name,
                homeDepartmentId: parentProject ? undefined : newProject.homeDepartmentId,
                startDate: newProject.startDate ? newProject.startDate : undefined,
                endDate: newProject.endDate ? newProject.endDate : undefined,
                parent: newProject.parent ? newProject.parent : undefined,
                projectResponsible: newProject.projectResponsible ? newProject.projectResponsible : undefined,
                sourcingResponsible: newProject.sourcingResponsible ? newProject.sourcingResponsible : undefined,
                projectType: newProject.projectType ? newProject.projectType : undefined,
              },
            });
            if ("error" in res) {
              toast({ status: "error", title: t("Could not create project") ?? "" });
              return;
            }
            setNewProject(initializeProject());
            setShowModal(false);
            goToProject(res.data.id);
          }}
        >
          {t("Save")}
        </Button>
      </Flex>
    </>
  );
};
